import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Popup hint: does not shift layout. Default: show on hover (desktop).
 * @param {string} text
 * @param {string} [placement='top'] - top | bottom | left | right
 * @param {'hover'|'press'} [mode='hover'] - press: tap/click icon, auto-hide, click outside closes
 * @param {number} [autoHideMs=5000] - for mode press: hide after N ms (0 = no timer)
 * @param {boolean} [wide=false] - wider bubble for long copy
 */
export default function HintPopup({ text, placement = 'top', mode = 'hover', autoHideMs = 5000, wide = false }) {
  const [visible, setVisible] = useState(false);
  const wrapRef = useRef(null);
  const hideTimerRef = useRef(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearHideTimer();
    setVisible(false);
  }, [clearHideTimer]);

  const showPress = useCallback(() => {
    clearHideTimer();
    setVisible(true);
    if (mode === 'press' && autoHideMs > 0) {
      hideTimerRef.current = setTimeout(() => {
        hideTimerRef.current = null;
        setVisible(false);
      }, autoHideMs);
    }
  }, [mode, autoHideMs, clearHideTimer]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  useEffect(() => {
    if (!visible || mode !== 'press') return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) hide();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') hide();
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [visible, mode, hide]);

  if (!text) return null;

  const contentClass = [
    'sft-hint-popup-content',
    `sft-hint-popup-${placement}`,
    wide ? 'sft-hint-popup-content--wide' : ''
  ]
    .filter(Boolean)
    .join(' ');

  if (mode === 'press') {
    return (
      <span ref={wrapRef} className="sft-hint-popup-wrap">
        <button
          type="button"
          className="sft-hint-popup-icon"
          aria-label="Open hint"
          aria-expanded={visible}
          onClick={() => {
            if (visible) hide();
            else showPress();
          }}
        >
          ℹ
        </button>
        {visible && (
          <span className={contentClass} role="tooltip">
            {text}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      className="sft-hint-popup-wrap"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className="sft-hint-popup-icon" aria-label={text}>
        ℹ
      </span>
      {visible && (
        <span className={contentClass} role="tooltip">
          {text}
        </span>
      )}
    </span>
  );
}
